import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import DashboardLayout from './components/Layout/DashboardLayout';
import RoleGuard from './guards/RoleGuard';
import Login from './pages/Login';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded page components — each becomes its own chunk
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tenants = lazy(() => import('./pages/Tenants'));
const TenantDetail = lazy(() => import('./pages/TenantDetail'));
const CreateTenantWizard = lazy(() => import('./pages/CreateTenantWizard'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'));
const Tickets = lazy(() => import('./pages/Tickets'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const SeedSuperAdmin = lazy(() => import('./pages/SeedSuperAdmin'));
const ProvisioningQueue = lazy(() => import('./pages/ProvisioningQueue'));
const SignupRequests = lazy(() => import('./pages/SignupRequests'));
const ChangeRequests = lazy(() => import('./pages/ChangeRequests'));

// Growth pages — lazy-loaded
const GrowthDashboard = lazy(() => import('./pages/growth/GrowthDashboard'));
const ProspectList = lazy(() => import('./pages/growth/ProspectList'));
const ProspectDetail = lazy(() => import('./pages/growth/ProspectDetail'));
const ProspectForm = lazy(() => import('./pages/growth/ProspectForm'));
const PipelineView = lazy(() => import('./pages/growth/PipelineView'));
const TasksPage = lazy(() => import('./pages/growth/TasksPage'));
const PilotsPage = lazy(() => import('./pages/growth/PilotsPage'));
const InvoicesPage = lazy(() => import('./pages/growth/InvoicesPage'));
const CustomersPage = lazy(() => import('./pages/growth/CustomersPage'));
const FunnelsPage = lazy(() => import('./pages/growth/FunnelsPage'));

const showSuperAdminSetup = import.meta.env.DEV || import.meta.env.VITE_ENABLE_SUPERADMIN_SETUP === 'true';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  
  return children;
};

const PageFallback = (
  <div className="flex items-center justify-center h-64">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-primary-600" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <Suspense fallback={PageFallback}>
    <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        {showSuperAdminSetup && <Route path="seed-superadmin" element={<SeedSuperAdmin />} />}
        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/new" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <CreateTenantWizard />
          </RoleGuard>
        } />
        <Route path="tenants/:id" element={<TenantDetail />} />
        <Route path="admin-users" element={
          <RoleGuard allowedRoles={['SuperAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <AdminUsers />
          </RoleGuard>
        } />
        <Route path="subscription-plans" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <SubscriptionPlans />
          </RoleGuard>
        } />
        <Route path="tickets" element={<Tickets />} />
        <Route path="audit-logs" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <AuditLogs />
          </RoleGuard>
        } />
        <Route path="provisioning" element={<ProvisioningQueue />} />
        <Route path="signup-requests" element={<SignupRequests />} />
        <Route path="change-requests" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <ChangeRequests />
          </RoleGuard>
        } />

        {/* Growth routes - restricted to SuperAdmin and BizCoreAdmin */}
        <Route path="growth" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <GrowthDashboard />
          </RoleGuard>
        } />
        <Route path="growth/prospects" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <ProspectList />
          </RoleGuard>
        } />
        <Route path="growth/prospects/new" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <ProspectForm />
          </RoleGuard>
        } />
        <Route path="growth/prospects/:id" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <ProspectDetail />
          </RoleGuard>
        } />
        <Route path="growth/pipeline" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <PipelineView />
          </RoleGuard>
        } />
        <Route path="growth/tasks" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <TasksPage />
          </RoleGuard>
        } />
        <Route path="growth/pilots" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <PilotsPage />
          </RoleGuard>
        } />
        <Route path="growth/invoices" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <InvoicesPage />
          </RoleGuard>
        } />
        <Route path="growth/customers" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <CustomersPage />
          </RoleGuard>
        } />
        <Route path="growth/funnels" element={
          <RoleGuard allowedRoles={['SuperAdmin', 'BizCoreAdmin']} fallback={<Navigate to="/dashboard" replace />}>
            <FunnelsPage />
          </RoleGuard>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

export default App;
